import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * "Email report link" dialog — generates a signed public URL to the
 * Amazon+Impact report and emails it. Token is 30d valid. Used when
 * leadership wants a read-only snapshot without an admin login.
 */
export default function ShareReportDialog({
  open, onOpenChange,
  recipients, onRecipientsChange,
  label, onLabelChange,
  sharing, setSharing,
  result, setResult,
}) {
  const handleSend = async () => {
    const list = recipients.split(",").map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error("Add at least one recipient"); return; }
    setSharing(true);
    try {
      const { data } = await API.post("/admin/reports/send", {
        recipients: list, expires_days: 30, label: label || "Report",
      });
      setResult(data);
      toast.success(`Sent to ${data.sent.length} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="sn-share-dialog">
        <DialogHeader>
          <DialogTitle>Email report link</DialogTitle>
          <DialogDescription>
            Recipients get a secure link that opens the Amazon + Impact report — no login required. Valid for 30 days.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Recipients (comma-separated)</Label>
              <Input value={recipients} onChange={(e) => onRecipientsChange(e.target.value)} placeholder="email@co.com, email2@co.com" className="mt-1" data-testid="sn-share-recipients" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Label (appears in email + header)</Label>
              <Input value={label} onChange={(e) => onLabelChange(e.target.value)} className="mt-1" data-testid="sn-share-label" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-emerald-700 font-medium">✓ Sent to {result.sent.length} recipient(s)</div>
            {result.failed?.length > 0 && (
              <div className="text-xs text-red-600">
                Failed: {result.failed.map((f) => f.email).join(", ")}
              </div>
            )}
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Report URL</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={result.url} readOnly className="font-mono text-xs" data-testid="sn-share-url" />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(result.url); toast.success("Link copied"); }}>Copy</Button>
              </div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sharing}>
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button
              onClick={handleSend}
              disabled={sharing || !recipients.trim()}
              className="bg-[#002FA7] hover:bg-[#002089] text-white"
              data-testid="sn-share-send-btn"
            >
              {sharing ? "Sending…" : "Send link"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
